<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb
          parentRouteName="BookingSelectList"
        />
      </div>
    </div>
    <b-card title="預約日曆">
      <b-overlay :show="showLoading" rounded="sm">
        <b-row no-gutters class="mt-2 mb-3">
          <b-col sm="4" lg="3">
            <b-form-select v-model="query.preset_id" :options="bookingPresetsOptions"/>
          </b-col>
          <b-button
            v-if="checkPermission([consts.BOOKING_CREATE])"
            class="flex-shrink-0 align-self-center ml-2"
            variant="primary"
            :to="{
              name: 'BookingCenterBookingCreate',
              params: { id: query.preset_id },
              query: { branch_id: query.branch_id },
            }"
          >
            <i class="fa fa-plus"></i>
            新增預約
          </b-button>
          <b-button
            v-b-toggle.collapse-1-inner
            variant="link"
            class="flex-shrink-0 ml-2"
          >
            進階搜尋<i class="fa fa-chevron-down ml-2"></i>
          </b-button>
          <h6 class="text-center mt-2 ml-auto">預約筆數：{{ fetchCount }} / 總筆數：{{ totalCount }}</h6>
        </b-row>
        <b-collapse id="collapse-1-inner">
          <div class="mt-2 mb-3 d-flex flex-column">
            <b-form-radio-group
              class="mb-2 d-flex align-items-center"
              v-model="staffResourceFilter"
              :options="[{ text: '顯示全部', value: 'all' }, { text: '已預約員工', value: 'booked' }]"
              @input="handleStaffResourceFilterChanged"
            ></b-form-radio-group>
            <AppMultiSelect
              class="mb-2"
              style="order: 0"
              v-model="query.status"
              :options="statusOptions"
              :disabled="showLoading || !this.query.preset_id"
            />
            <AppSingleSelect
              v-if="showBranchSelect"
              class="mb-2"
              :style="{ order: deepGet(bookingPresetModel, 'wizardFieldOrder.branch', 1) }"
              v-model="branch"
              :options="branchOptions"
              @input="handleBranchFilterChanged"
              :disabled="showLoading || !this.checkPermission([consts.BOOKING_LIST_BRANCH])"
            />
            <b-select
              v-if="showStaffSelect"
              class="mb-2"
              :style="{ order: deepGet(bookingPresetModel, 'wizardFieldOrder.staff', 2) }"
              :options="staffOptions"
              v-model="query.staff_id"
              @input="handleStaffFilterChanged"
              :disabled="showLoading || staffOptions.length <= 1"
            ></b-select>
            <b-select
              v-if="showServiceSelect"
              class="mb-2"
              :style="{ order: deepGet(bookingPresetModel, 'wizardFieldOrder.service', 3) }"
              :options="serviceOptions"
              v-model="query.service_id"
              @input="handleServiceFilterChanged"
              :disabled="showLoading"
            ></b-select>
          </div>
        </b-collapse>
        <FullCalendar :options="calendarOptions" ref="fullCalendar">
          <template v-slot:eventContent="arg">
            <span class="text-center">{{ arg.event.extendedProps.title }}</span>
            <div :id="`tooltip_${arg.event.extendedProps.id}`" class="floating-tooltip" role="tooltip">
              <div class="popover" :class="`bs-popover-${placement}`">
                <h3 class="popover-header">{{ arg.event.extendedProps.title }}</h3>
                <div class="popover-body">
                  <div v-for="field in bookingDetailFields" :key="field.key">
                    <span>{{ field.label }}：{{ deepGet(arg.event.extendedProps, field.key) }}</span>
                  </div>
                  <router-link :to="{
                    name: 'BookingCenterBookingEdit',
                    params: {
                      id: query.preset_id,
                      booking_id: arg.event.extendedProps.id
                    }
                  }"><span @click="() => closeTooltip()">查看明細</span></router-link>
                </div>
                <span :id="`arrow_${arg.event.extendedProps.id}`" class="arrow"></span>
              </div>
            </div>
          </template>
        </FullCalendar>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import bookingApi from '@/apis/booking'
import bookingPresetApi from '@/apis/booking-preset'
import collectionApi from "@/apis/collection"
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import zhTwLocale from '@fullcalendar/core/locales/zh-tw'
import { arrow, autoPlacement, computePosition, flip, offset, shift } from '@floating-ui/dom'
import BookingPresetDataModel from "@/models/BookingPreset.model"
import DynamicFormField from "@/models/DynamicFormField.model.js";
import BookingPresenter from "@/presenters/Booking.presenter.js"
import BookingBusinessLogic from "@/utils/booking/BookingBusinessLogic"
import ServiceDataFetcher from "@/utils/booking/ServiceDataFetcher"
import StaffDataFetcher from "@/utils/booking/StaffDataFetcher"
import _ from "lodash"
import moment from "moment"
import Vue from "vue"
import { mapGetters } from "vuex";
import * as consts from "@/consts";
import PermissionChecker from '@/utils/PermissionChecker';
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";
import AppMultiSelect from "@/components/AppMultiSelect";

const servicesForFilterDataFetcher = Vue.observable(new ServiceDataFetcher())
servicesForFilterDataFetcher.injectCollectionApi(collectionApi)

const staffsForFilterDataFetcher = Vue.observable(new StaffDataFetcher())
staffsForFilterDataFetcher.injectBookingPresetApi(bookingPresetApi)
const staffsForBranchDataFetcher = Vue.observable(new StaffDataFetcher())
staffsForBranchDataFetcher.injectBookingPresetApi(bookingPresetApi)

const STORAGE_KEY_CALENDAR_DATE_SET = 'booking_calendar_date_set';
const STORAGE_KEY_LAST_PRESET_ID = 'booking_calendar_last_preset_id';
const STORAGE_KEY_STATUS_FILTER = 'booking_calendar_status_filter';

function clickOutside(element, callback) {
  function handleClick(event) {
    if (!element.contains(event.target)) {
      callback(event)
      document.removeEventListener('click', handleClick)
    }
  }
  document.addEventListener('click', handleClick)
}

export default {
  components: {
    FullCalendar,
    AppSingleSelect,
    BreadCrumb,
    AppMultiSelect
  },
  data() {
    return {
      consts: consts,
      isFetchingPresets: false,
      isFetchingBookings: false,
      isFetchingCollectionsOfCurrentBookingPresetFields: false,
      bookingPresets: [],
      collectionsOfCurrentBookingPresetFields: [],
      bookings: [],
      placement: 'top',
      bookingStatusOptionResponse: null,
      calendarOptions: {
        plugins: [
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin,
          resourceTimeGridPlugin,
        ],
        datesSet: this.handleDatesSet,
        locales: [zhTwLocale],
        locale: 'zh-tw',
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,resourceTimeGridDay'
        },
        firstDay: 1,
        initialView: localStorage.getItem(STORAGE_KEY_CALENDAR_DATE_SET) || 'resourceTimeGridDay',
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        slotMinTime: '0:00:00',
        slotMaxTime: '24:00:00',
        slotLabelFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        },
        weekends: true,
        events: [],
        eventClick: (arg) => {
          const button = arg.el
          const tooltip = document.querySelector(`#tooltip_${arg.event.extendedProps.id}`)
          this.currentTooltip = tooltip
          const arrowEl = document.querySelector(`#arrow_${arg.event.extendedProps.id}`)

          tooltip.style.display = 'block'
          document.body.appendChild(tooltip)

          computePosition(button, tooltip, {
            placement: this.placement,
            middleware: [
              offset(6),
              flip(),
              shift(),
              autoPlacement({
                alignment: 'start',
                allowedPlacements: ['top', 'bottom'],
              }),
              arrow({ element: arrowEl, padding: 8 })],
            strategy: 'fixed',
          }).then(({ x, y, middlewareData }) => {
            Object.assign(tooltip.style, { left: `${x}px`, top: `${y}px` })

            const { x: arrowX, y: arrowY } = middlewareData.arrow
            Object.assign(arrowEl.style, {
              left: arrowX != null ? `${arrowX}px` : '',
              top: arrowY != null ? `${arrowY}px` : '',
              right: '',
              bottom: '',
            })
            setTimeout(() => {
              clickOutside(tooltip, () => {
                tooltip.style.display = 'none'
              })
            })
          })
        },
        resourceAreaHeaderContent: '員工',
        resources: this.calendarResources,
      },
      currentTooltip: null,
      query: {
        preset_id: localStorage.getItem(STORAGE_KEY_LAST_PRESET_ID) || null,
        service_id: null,
        branch_id: null,
        staff_id: null,
        status: localStorage.getItem(STORAGE_KEY_STATUS_FILTER) ? JSON.parse(localStorage.getItem(STORAGE_KEY_STATUS_FILTER)) : [],
        start_at: null,
        end_at: null,
      },
      fetchCount: 0,
      totalCount: 0,
      branch: {},
      staffResourceFilter: 'all',
    }
  },
  computed: {
    ...mapGetters('general', [
      'myBranches',
      'currentBranch'
    ]),
    queryWatcher() {
      const statusStr = Array.isArray(this.query.status) ? this.query.status.join(',') : this.query.status
      return `${this.query.preset_id}-${statusStr}-${this.query.service_id}-${this.query.branch_id}-${this.query.staff_id}`;
    },
    serviceIdQueryInArray() {
      if (!this.query.service_id) {
        return []
      }
      return Array.isArray(this.query.service_id) ? this.query.service_id : [this.query.service_id]
    },
    currentBookingPreset() {
      return this.bookingPresets.find(booking => booking.id === this.query.preset_id)
    },
    bookingPresetModel() {
      return new BookingPresetDataModel(this.currentBookingPreset)
    },
    bookingBusinessLogic() {
      return new BookingBusinessLogic(this.bookingPresetModel)
    },
    bookingPresetsOptions() {
      return [
        { text: '請選擇', value: null },
        ...this.bookingPresets.map(preset => ({ text: preset.name, value: preset.id }))
      ]
    },
    showBranchSelect() {
      return this.bookingPresetModel.branchFieldId
    },
    showStaffSelect() {
      return this.bookingPresetModel.isWizardMode
    },
    showServiceSelect() {
      return this.bookingPresetModel.isWizardMode && this.bookingPresetModel.collectionIdForService
    },
    serviceOptions() {
      return [
        { value: null, text: "選擇服務" },
        ...servicesForFilterDataFetcher.services.map(service => ({ value: service.id, text: service.title }))
      ]
    },
    staffOptions() {
      return [
        { value: null, text: '請選擇員工' },
        ...staffsForFilterDataFetcher.staffs.map(staff => ({ value: staff.id, text: staff.name }))
      ]
    },
    branchOptions() {
      return [
        { value: null, text: '請選擇分店' },
        ...this.myBranches.map(branch => ({ value: branch.id, text: `${branch.branch_code} ${branch.name}` }))
      ]
    },
    statusOptions() {
      return [
        ..._.get(this.bookingStatusOptionResponse, 'data.status_mapping', [])
          .map(status => ({ value: status, text: status }))
      ]
    },
    bookingDetailFields() {
      const fields = DynamicFormField.fromArray(_.get(this.currentBookingPreset, 'config.fields', []))
        .filter(fieldModel => fieldModel.list_hidden !== true)
        .map(fieldModel => ({ key: fieldModel._id, label: fieldModel.title }))

      if (this.bookingPresetModel.isWizardMode) {
        if (this.bookingPresetModel.branchSelectionIsEnabled) {
          fields.push({ key: "branch_name", label: "分店" })
        }

        if (this.bookingPresetModel.staffSelectionIsEnabled) {
          fields.push({ key: "staff_name", label: "員工" })
        }

        if (this.bookingPresetModel.collectionIdForService) {
          fields.push({ key: 'service_name', label: '服務' })
        }
      }

      return fields
    },
    calendarEvents() {
      return this.bookings.map(booking => {
        const bookingPresenter = BookingPresenter.create(booking, this.currentBookingPreset, this.collectionsOfCurrentBookingPresetFields)

        return {
          id: booking.id,
          resourceId: booking.staff_id,
          title: this.showLoading ? '' : bookingPresenter.title,
          start: booking.start_at,
          end: booking.end_at,
          extendedProps: {
            id: booking.id,
            ...bookingPresenter.fieldDataInString,
            branch_name: bookingPresenter.branchName,
            staff_name: bookingPresenter.staffName,
            service_name: bookingPresenter.serviceName,
            title: this.showLoading ? '' : bookingPresenter.title
          }
        }
      })
    },
    calendarResources() {
      if (this.staffResourceFilter === 'booked') {
        return _.uniqBy(this.bookings, 'staff_id')
          .map(booking => ({
            id: booking.staff_id,
            title: booking.staff?.name,
            // eventColor: 'green',
        }))
      }

      return staffsForBranchDataFetcher.staffs
        .map(staff => ({
          id: staff.id,
          title: staff.name,
          // eventColor: 'green',
        }))
    },
    showLoading() {
      return this.isFetchingPresets || this.isFetchingBookings || this.isFetchingCollectionsOfCurrentBookingPresetFields
    },
  },
  async mounted() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
    await this.fetchBookingPresets()
    this.query.preset_id = this.query.preset_id ?? this.bookingPresets[0]?.id
    if (this.showBranchSelect) {
      this.setBranchFilterToCurrentBranch()
    } else {
      this.$set(this.query, 'branch_id', null)
      this.branch = {}
      this.fetchBookings();
    }
  },
  watch: {
    queryWatcher: {
      handler() {
        localStorage.setItem(STORAGE_KEY_LAST_PRESET_ID, this.query.preset_id);
        localStorage.setItem(STORAGE_KEY_STATUS_FILTER, JSON.stringify(this.query.status))
        updateUrlWithoutReload(this.query, this.$store, this.$route)
        if (this.query.preset_id) {
          this.fetchBookings();
        } else {
          this.bookings = []
          this.bookingStatusOptionResponse = null
        }
      },
    },
    currentBookingPreset: {
      async handler(bookingPreset) {
        if (!bookingPreset) {
          this.calendarOptions.events = []
          this.calendarOptions.resources = []
          return
        }
        servicesForFilterDataFetcher.setServiceFieldMappings(this.bookingPresetModel.serviceFieldMappings)
        if (this.showBranchSelect) {
          this.setBranchFilterToCurrentBranch()
        }
        this.getStatusOptions()
        await this.fetchCollectionsOfCurrentBookingPresetFields()
        this.updateCalenderOptions()

        this.query.status = [];
      },
      immediate: true
    },
  },
  methods: {
    deepGet: _.get,
    async handleBranchFilterChanged() {
      this.query.branch_id = this.branch.value

      if (this.bookingBusinessLogic.servicesAreBoundToBranches()) {
        servicesForFilterDataFetcher.fetchServicesByBranch(this.bookingPresetModel.collectionIdForService, this.query.branch_id)
        if (this.branch.value === null) {
          this.query.service_id = null
          this.handleServiceFilterChanged()
        }
      }

      if (this.bookingBusinessLogic.staffsAreBoundToBranches()) {
        staffsForFilterDataFetcher.fetchStaffsByServices(this.serviceIdQueryInArray, this.query.branch_id, this.query.preset_id)
        if (this.branch.value === null) {
          this.query.staff_id = null
          this.handleStaffFilterChanged()
        }
      }

      staffsForBranchDataFetcher.fetchStaffsByBranch(this.query.branch_id, this.query.preset_id)
    },
    async handleStaffFilterChanged() {
      if (this.bookingBusinessLogic.servicesAreBoundToStaffs()) {
        servicesForFilterDataFetcher.fetchServicesByStaff(this.bookingPresetModel.collectionIdForService, this.query.staff_id)
        if (this.query.staff_id === null) {
          this.query.service_id = null
          this.handleServiceFilterChanged()
        }
      }
    },
    async handleServiceFilterChanged() {
      if (this.bookingBusinessLogic.staffsAreBoundToServices()) {
        staffsForFilterDataFetcher.fetchStaffsByServices(this.serviceIdQueryInArray, this.query.branch_id, this.query.preset_id)
        if (this.query.service_id === null) {
          this.query.staff_id = null
          this.handleStaffFilterChanged()
        }
      }
    },
    async handleDatesSet(dateInfo) {
      localStorage.setItem(STORAGE_KEY_CALENDAR_DATE_SET, dateInfo.view.type);
      this.query.start_at = moment(dateInfo.startStr).format('YYYY-MM-DD 00:00:00')
      this.query.end_at = moment(dateInfo.endStr).format('YYYY-MM-DD 00:00:00')
      if (this.query.preset_id) {
        await this.fetchBookings()
      }
    },
    handleStaffResourceFilterChanged() {
      this.updateCalenderOptions()
    },
    async getStatusOptions() {
      this.bookingStatusOptionResponse = await bookingApi.getBookingOptions(this.query.preset_id)
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker;
      return checker.check(permissions);
    },
    async fetchBookingPresets() {
      try {
        this.isFetchingPresets = true
        const response = await bookingApi.getBookingPresets({
          page: 1,
          per_page: 20,
          status: "published"
        });
        this.bookingPresets = response.data.data
      } catch (error) {
        console.error(error)
        this.$swal("錯誤", "讀取預約設定檔發生錯誤", "error")
      } finally {
        this.isFetchingPresets = false
      }
    },
    async fetchBookings() {
      try {
        this.isFetchingBookings = true
        const response = await bookingApi.getBookings(this.query.preset_id, {
          start_at: this.query.start_at,
          end_at: this.query.end_at,
          page: 1,
          per_page: 100,
          keyword: '',
          branch_id: this.query.branch_id,
          staff_id: this.query.staff_id,
          service_id: this.query.service_id,
          status: this.query.status.map(s => typeof s === 'string' ? s : s.value),
          with_customer_profile: true,
        })
        this.fetchCount = response.data.meta.to ?? 0
        this.totalCount = response.data.meta.total ?? 0
        this.bookings = _.get(response, 'data.data', [])
        this.updateCalenderOptions()
      } catch (error) {
        console.error(error)
        this.$swal("錯誤", "讀取預約資料發生錯誤", "error")
      } finally {
        this.isFetchingBookings = false
      }
    },
    async fetchCollectionsOfCurrentBookingPresetFields() {
      this.isFetchingCollectionsOfCurrentBookingPresetFields = true
      try {
        this.collectionsOfCurrentBookingPresetFields = await Promise.all(
          DynamicFormField.fromArray(_.get(this.currentBookingPreset, 'config.fields', []))
            .filter(fieldModel => fieldModel.isCollectionField() && fieldModel.collection_id)
            .map(async fieldModel => _.get(await collectionApi.getCollection(fieldModel.collection_id, { with_all_items: true }), 'data.data'))
        )
      } catch (error) {
        console.error(error)
        this.$swal("錯誤", "讀取資料集發生錯誤", "error")
      } finally {
        this.isFetchingCollectionsOfCurrentBookingPresetFields = false
      }
    },
    setBranchFilterToCurrentBranch() {
      if (this.currentBranch) {
        this.query.branch_id = this.currentBranch.id
        this.branch = this.branchOptions.find(branch => branch.value === this.currentBranch.id)

        if (this.bookingBusinessLogic.servicesAreBoundToBranches()) {
          servicesForFilterDataFetcher.fetchServicesByBranch(this.bookingPresetModel.collectionIdForService, this.query.branch_id)
        }

        if (this.bookingBusinessLogic.staffsAreBoundToBranches()) {
          staffsForFilterDataFetcher.fetchStaffsByServices(this.serviceIdQueryInArray, this.query.branch_id, this.query.preset_id)
        }

        staffsForBranchDataFetcher.fetchStaffsByBranch(this.query.branch_id, this.query.preset_id)
      }
    },
    updateCalenderOptions() {
      this.calendarOptions.events = this.calendarEvents
      this.calendarOptions.resources = this.calendarResources
      this.calendarOptions.slotMinTime = this.bookingPresetModel.openingTime || '00:00'
      this.calendarOptions.slotMaxTime = this.bookingPresetModel.closingTimeDaily || '24:00'
    },
    closeTooltip() {
      if (this.currentTooltip) {
        this.currentTooltip.style.display = 'none'
      }
    },
  }
}
</script>

<style scoped lang="scss">
.floating-tooltip {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: max-content;
  padding: 5px;
  font-weight: bold;
  border-radius: 4px;
  font-size: 90%;
  z-index: 10000;

  .popover {
    position: relative;
  }
}

.row-searchbar {
  width: 80%;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
}

::v-deep.fc {
  .fc-daygrid-dot-event {
    overflow: hidden;
  }

  @media screen and (max-width: 768px) {
    .fc-header-toolbar {
      display: grid;
      grid-template-areas:
        "date      date "
        "navigator range";

      .fc-toolbar-chunk {
        &:first-child {
          grid-area: navigator;
        }

        &:nth-child(2) {
          grid-area: date;
          margin-bottom: 16px;
          text-align: center;
        }

        &:nth-child(3) {
          grid-area: range;
        }
      }
    }

    .fc-view-harness {
      min-height: 600px;
    }
  }
}
</style>
